/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VendorSelect.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Device Models module.
----------------------------------------------------------------*/

import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, Loader2 } from 'lucide-react';

export interface VendorSelectProps {
    value: string;
    onChange: (value: string) => void;
    vendors: { id: number | string; name: string }[];
    error?: string;
    isLoading?: boolean;
    loadError?: string | null;
    disabled?: boolean;
}

export const VendorSelect = ({ value, onChange, vendors, error, isLoading, loadError, disabled }: VendorSelectProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredVendors = vendors.filter(v =>
        v.name.toLowerCase().includes(search.toLowerCase())
    );

    const selectedVendor = vendors.find(v => String(v.id) === String(value));

    // Handle initial loading
    if (isLoading && vendors.length === 0) {
        return (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground h-10 px-3 border border-input border-dashed rounded-md bg-muted/10">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading vendors...</span>
            </div>
        );
    }

    // Handle load error
    if (loadError) {
        return (
            <div className="text-sm text-destructive h-10 px-3 border border-destructive/20 rounded-md bg-destructive/10 flex items-center">
                <span>Error: {loadError}</span>
            </div>
        );
    }

    return (
        <div className="relative w-full" ref={containerRef}>
            <button
                type="button"
                onClick={() => !disabled && setOpen(!open)}
                disabled={disabled}
                className={`flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${disabled ? 'bg-muted/50' : ''} ${error ? 'border-red-500' : 'border-input'
                    }`}
            >
                <span className={selectedVendor ? '' : 'text-muted-foreground'}>
                    {selectedVendor ? selectedVendor.name : 'Select Vendor'}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95">
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Search vendor..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1">
                        {filteredVendors.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No vendor found.
                            </div>
                        ) : (
                            filteredVendors.map((vendor) => (
                                <div
                                    key={vendor.id}
                                    onClick={() => {
                                        onChange(String(vendor.id));
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                    className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${String(value) === String(vendor.id) ? 'bg-accent text-accent-foreground' : ''
                                        }`}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${String(value) === String(vendor.id)
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                            }`}
                                    />
                                    {vendor.name}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            {error && <span className="text-xs text-red-500 mt-1 block">{error}</span>}
        </div>
    );
};

export default VendorSelect;
